/**
 * ConciergeSearchBar — pill-shaped hero search bar shared across surfaces.
 *
 * Founder ruling 18 Feb 2026 PM-9: /escape must mirror /enjoy's "the bar
 * IS the page" search rhythm. Same border-radius, same focus brass, same
 * "Find it" / "Find escapes" right-aligned CTA on a pill input.
 *
 * The bar supports an OPTIONAL secondary field slot (used by /escape for
 * "When?" with a date popover). When secondaryField is omitted, the bar
 * is a single-input pill — identical visual rhythm to /enjoy.
 *
 * Props:
 *   value          — controlled search string
 *   onChange       — (string) => void
 *   onSubmit       — () => void  (Enter or button)
 *   placeholder    — input placeholder (string)
 *   submitLabel    — CTA label, default "Find it"
 *   loading        — bool · disables submit + swaps label to loadingLabel
 *   loadingLabel   — string · default "Searching…"
 *   secondaryField — optional ReactNode rendered between the input
 *                    and submit button (e.g. /escape's "When?" trigger)
 *   leadIcon       — optional lead icon node (defaults to ⌕)
 *   submitDisabled — explicit disable flag (in addition to loading)
 *   testid         — root testid (default "concierge-search-bar")
 *   compact        — bool · slimmer pill for in-results sticky usage
 */
import React, { useState } from "react";

export default function ConciergeSearchBar({
    value,
    onChange,
    onSubmit,
    placeholder = "Search…",
    submitLabel = "Find it",
    loading = false,
    loadingLabel = "Searching…",
    secondaryField,
    leadIcon,
    submitDisabled = false,
    testid = "concierge-search-bar",
    compact = false,
}) {
    const [focused, setFocused] = useState(false);
    const disabled = loading || submitDisabled || !String(value || "").trim();

    return (
        <form
            data-testid={testid}
            className="lc-search-pill"
            onSubmit={(e) => {
                e.preventDefault();
                if (!disabled) onSubmit?.();
            }}
            style={{
                display: "flex",
                alignItems: "center",
                gap: 0,
                background: "var(--t-card, #FCFAF4)",
                border: `1.5px solid ${focused ? "var(--t-brass, #8B6914)" : "var(--t-line2, #D8CDB7)"}`,
                borderRadius: 999,
                padding: compact ? 4 : 6,
                width: "100%",
                position: "relative",
                boxShadow: "0 1px 0 rgba(33,28,21,0.04)",
                transition: "border-color 200ms ease",
            }}
        >
            <div className="lc-search-row">
                <span
                    aria-hidden="true"
                    style={{
                        color: focused ? "var(--t-brass, #8B6914)" : "var(--t-ink3, #8A8071)",
                        fontSize: compact ? 16 : 18,
                        paddingLeft: compact ? 12 : 16,
                    }}
                >
                    {leadIcon ?? "⌕"}
                </span>
                <input
                    type="search"
                    placeholder={placeholder}
                    value={value}
                    onChange={(e) => onChange?.(e.target.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    autoComplete="off"
                    data-testid={`${testid}-input`}
                    className="lc-search-primary"
                    style={{
                        flex: 1,
                        minWidth: 0,
                        padding: compact ? "8px 12px" : "10px 14px",
                        border: 0,
                        outline: 0,
                        background: "transparent",
                        fontSize: compact ? 14.5 : 17,
                        fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                        color: "var(--t-ink, #211C15)",
                    }}
                />
            </div>
            {secondaryField && (
                <div className="lc-search-secondary">
                    <div
                        aria-hidden="true"
                        style={{
                            width: 1,
                            alignSelf: "stretch",
                            margin: "10px 0",
                            background: "var(--t-line, #E5DCCB)",
                        }}
                    />
                    {secondaryField}
                </div>
            )}
            <button
                type="submit"
                data-testid={`${testid}-submit`}
                disabled={disabled}
                className="lc-search-submit"
                onMouseDown={(e) => e.preventDefault()}
                style={{
                    background: "var(--t-ink, #211C15)",
                    color: "var(--t-paper2, #FBF8F1)",
                    borderRadius: 999,
                    border: 0,
                    padding: compact ? "9px 18px" : "13px 24px",
                    minHeight: compact ? 36 : 44,
                    fontSize: compact ? 13 : 14,
                    fontWeight: 600,
                    letterSpacing: "0.01em",
                    cursor: disabled ? "not-allowed" : "pointer",
                    whiteSpace: "nowrap",
                    opacity: disabled ? 0.55 : 1,
                    touchAction: "manipulation",
                    transition: "opacity 150ms ease",
                }}
            >
                {loading ? loadingLabel : submitLabel}
            </button>
        </form>
    );
}
